// Colocated with App.tsx per spec §4.3
// (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md): app/ has no
// slices, and this hook drives App.tsx only, so it stays beside its component.
import { fetchLatestAppVersion, CONFIG } from '@/entities/user';
import { clearCookies } from '@/app/authentication-gate/authenticationGate.handler';
import useRelocatedSocketListeners from '@/app/socket/useRelocatedSocketListeners';
import { EMITTER_CONSTANTS } from '@/shared/config';
import {
  compareVersionApp,
  isWebViewCreateStudy,
  auth,
  emitter,
  storage,
  STORAGE_KEYS,
} from '@/shared/lib';
import { useEmitter, useMergeState, useConstructor } from '@/shared/lib/customHooks';
import { message, notification } from 'antd';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { setLastAppVersionCheckAt, canRunSocketVersionCheck } from './lib/app-version-check';

/**
 * Wires the application shell: socket listeners, message and notification context holders, auth tracking and the new-version modal. Mounts the relocated socket listeners, seeds isAuthenticated from localStorage and refreshes it on UPDATE_TOKEN_LOCAL_STORAGE, clears cookies on the web-redirect entry point, checks the app version on mount and on SOCKET_CONNECTED when the interval allows, and records the pathname as the post-login redirect.
 *
 * @returns The context holders, modal state, update-modal suppression flag and the reload handler.
 */
export const useApp = () => {
  // Mounted here rather than in Main: AuthenticationGate connects the socket
  // before it renders its children, so listeners attached from Main would miss
  // everything pushed between connect and the me/facilities queries resolving.
  useRelocatedSocketListeners();

  const [, contextHolder] = message.useMessage();
  const [, notiContextHolder] = notification.useNotification({
    stack: { threshold: 3 },
  });

  const [isAuthenticated, setIsAuthenticated] = useState(
    !!storage.get(STORAGE_KEYS.isSuccess) || !!storage.get(STORAGE_KEYS.accessToken),
  );

  const [state, setState] = useMergeState({
    isOpenModal: false,
  });

  /**
   * Indicates the latest-update modal must be suppressed for the current entry point. True when the pathname contains '/web-redirect' or the app is running as the create-study webview.
   *
   * @returns True when the update modal must not be shown.
   */
  const preventDisplayLatestUpdateModal =
    _.includes(window.location.pathname, '/web-redirect') || isWebViewCreateStudy();

  /**
   * Recomputes whether the app treats the visitor as authenticated after the auth entries in localStorage changed. Reads accessToken and isSuccess and sets isAuthenticated when either one is present.
   *
   * @returns Nothing.
   */
  const changeLocalStorage = () => {
    const newToken = storage.get(STORAGE_KEYS.accessToken);
    const isSuccess = storage.get(STORAGE_KEYS.isSuccess);
    setIsAuthenticated(!!isSuccess || !!newToken);
  };

  /**
   * Compares the running build against the latest published Web version of bioflux-portal. A newer remote version opens the update modal when the flag is already set, otherwise it records the flag and reloads immediately; a matching version clears the flag and a failed fetch is only logged.
   *
   * @returns A promise resolving once the version check has been handled.
   */
  const handleCheckAppVersion = async () => {
    try {
      const latestVersion = await fetchLatestAppVersion('bioflux-portal', 'Web');
      if (!_.isNil(latestVersion)) {
        if (compareVersionApp(latestVersion, __APP_VERSION__)) {
          if (auth.isShowLatestVersionModal()) {
            setState({ isOpenModal: true });
          } else {
            auth.setIsShowLatestVersionModal(true);
            window.location.reload();
          }
        } else {
          auth.setIsShowLatestVersionModal(false);
        }
      }
    } catch (error) {
      // Do nothing
      console.error('Failed to fetch latest app version: ', error);
    }
  };

  /**
   * Confirms the new-version modal. Reloads the page so the newly deployed build is fetched.
   *
   * @returns Nothing.
   */
  const onClickRightButton = () => {
    window.location.reload();
  };

  useConstructor(() => {
    if (preventDisplayLatestUpdateModal && !auth.isLoginWithBiocarePortal()) {
      clearCookies();
    }
  });

  useConstructor(() => {
    emitter.addListener(EMITTER_CONSTANTS.UPDATE_TOKEN_LOCAL_STORAGE, (msg: any) => {
      if (msg) {
        changeLocalStorage();
      }
    });
  });

  useEffect(() => {
    handleCheckAppVersion();
    setLastAppVersionCheckAt();
  }, []);

  useEmitter(
    EMITTER_CONSTANTS.SOCKET_CONNECTED,
    () => {
      if (!canRunSocketVersionCheck()) {
        return;
      }

      setLastAppVersionCheckAt();
      handleCheckAppVersion();
    },
    [],
  );

  if (
    !isAuthenticated &&
    !['newpw', 'verifyEmail', 'completeSupportEmail', 'profile'].some((word) =>
      window.location.pathname.includes(word),
    )
  ) {
    CONFIG.HOST_REDIRECT_URL = window.location.pathname;
  }

  return {
    contextHolder,
    notiContextHolder,
    preventDisplayLatestUpdateModal,
    state,
    onClickRightButton,
  };
};
